class CrownPressFeed extends HTMLElement {
  #controller;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    window.addEventListener('popstate', this.#onPopState);
    this.#load(this.#currentPage());
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.removeEventListener('click', this.#onClick);
    window.removeEventListener('popstate', this.#onPopState);
  }

  #currentPage() {
    return this.#pageNumber(new URL(window.location.href).searchParams.get('press_page'));
  }

  #pageNumber(value) {
    return /^\d+$/.test(value ?? '') && Number(value) > 0 ? Number(value) : 1;
  }

  #onPopState = () => this.#load(this.#currentPage());

  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('[data-press-retry]')) {
      this.#load(this.#currentPage());
      return;
    }
    const link = event.target.closest('[data-press-pagination] a');
    if (!link || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const url = new URL(link.href);
    if (url.origin !== window.location.origin) return;
    event.preventDefault();
    this.#load(this.#pageNumber(url.searchParams.get('page')), true);
  };

  async #load(page, navigate = false) {
    this.#controller?.abort();
    const controller = new AbortController();
    this.#controller = controller;
    const timeout = setTimeout(() => controller.abort(), 15000);
    const status = this.querySelector('[data-press-status]');
    const loading = this.querySelector('[data-press-loading]');
    const error = this.querySelector('[data-press-error]');
    const retry = this.querySelector('[data-press-retry]');
    const content = this.querySelector('[data-press-content]');
    this.setAttribute('aria-busy', 'true');
    status.hidden = false;
    loading.hidden = false;
    error.hidden = true;
    retry.hidden = true;

    try {
      const url = new URL(this.dataset.feedUrl, window.location.origin);
      if (url.origin !== window.location.origin) throw new Error('Invalid press feed origin');
      url.searchParams.set('section_id', 'crown-press-articles');
      url.searchParams.set('page', String(page));
      const preview = new URL(window.location.href).searchParams.get('preview_theme_id');
      if (preview) url.searchParams.set('preview_theme_id', preview);
      const response = await fetch(url, { signal: controller.signal, cache: 'no-cache' });
      if (!response.ok) throw new Error(`Press feed HTTP ${response.status}`);
      const document = new DOMParser().parseFromString(await response.text(), 'text/html');
      const results = document.querySelector('[data-press-results]');
      if (!results) throw new Error('Missing press feed');
      if (controller !== this.#controller || !this.isConnected) return;
      content.replaceChildren(results);
      status.hidden = true;
      if (navigate) {
        const pageUrl = new URL(window.location.href);
        if (page === 1) pageUrl.searchParams.delete('press_page');
        else pageUrl.searchParams.set('press_page', String(page));
        window.history.pushState(null, '', pageUrl);
        results.focus({ preventScroll: true });
        results.scrollIntoView({ block: 'start' });
      }
    } catch {
      if (controller !== this.#controller || !this.isConnected) return;
      loading.hidden = true;
      error.hidden = false;
      retry.hidden = false;
    } finally {
      clearTimeout(timeout);
      if (controller === this.#controller) this.setAttribute('aria-busy', 'false');
    }
  }
}

if (!customElements.get('crown-press-feed')) customElements.define('crown-press-feed', CrownPressFeed);
